import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import {
  KoobButton,
  KoobInputCheckbox,
  KoobInputDateField,
  KoobInputSelect,
  KoobModal,
} from '@koob/ui';
import { formatDate, toDateString } from '@koob/dates';
import { getFolderTravelerRow } from '@koob/utils';
import FolderFormTextField from './FolderFormTextField';

export interface FolderTravelerModalTraveler {
  id?: string;
  gender?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  birthdate?: string | null;
  /** False for an approximate (estimated) child birthdate. */
  ageIsExact?: boolean | null;
  kind?: string | null;
  nationality?: string | null;
  passportNumber?: string | null;
  passportExpirationDate?: string | null;
}

export interface FolderTravelerModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** The traveler to edit; none to add one. */
  traveler?: FolderTravelerModalTraveler | null;
  departureDate?: string | null;
  returnDate?: string | null;
  nationalityOptions: { value: string; label: string }[];
  onSubmit: (traveler: FolderTravelerModalTraveler) => Promise<void> | void;
}

interface FormValues {
  gender: string | null;
  firstName: string;
  lastName: string;
  birthdate: string | Date | null;
  ageIsExact: boolean;
  nationality: string | null;
  passportNumber: string;
  passportExpirationDate: string | Date | null;
}

const GENDERS = ['male', 'female', 'non_binary'];
const GENDER_LABEL_KEY = {
  male: 'misc:genders.male',
  female: 'misc:genders.female',
  non_binary: 'misc:genders.other',
};

// Mockup captions: small uppercase section titles, 12px field labels.
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="m-0 text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
    {children}
  </h3>
);

const Field = ({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="flex min-w-0 flex-col gap-1.5">
    <span className="text-xs font-semibold text-gray-700">{label}</span>
    {children}
  </div>
);

// The date picker hands back a Date: send the calendar day ('YYYY-MM-DD'), a
// serialized Date could land on the previous day in UTC.
const toApiDate = (value: string | Date | null | undefined) =>
  value ? toDateString(value) : null;

/**
 * KD-3842 edit / add traveler: identity (gender, names, birthdate with the age
 * and Adult / Child at departure, nationality) and passport. The birthdate is
 * sent only when it changes. For a child, "Definitive birth date" is sent
 * explicitly with it: on its own, the API reads any sent birthdate as exact.
 */
export default function FolderTravelerModal({
  open,
  setOpen,
  traveler,
  departureDate,
  returnDate,
  nationalityOptions,
  onSubmit,
}: FolderTravelerModalProps) {
  const { t } = useTranslation('folders');
  const isEdit = Boolean(traveler?.id);
  const current = traveler
    ? getFolderTravelerRow(traveler, { departureDate, returnDate })
    : null;

  const initialValues: FormValues = {
    gender: traveler?.gender ?? null,
    firstName: traveler?.firstName ?? '',
    lastName: traveler?.lastName ?? '',
    birthdate: traveler?.birthdate ?? null,
    ageIsExact: Boolean(traveler?.ageIsExact),
    nationality: traveler?.nationality ?? null,
    passportNumber: traveler?.passportNumber ?? '',
    passportExpirationDate: traveler?.passportExpirationDate ?? null,
  };

  const handleSubmit = async (values: FormValues) => {
    const birthdateChanged =
      !isEdit ||
      toApiDate(values.birthdate) !== toApiDate(initialValues.birthdate);
    const isChild = getFolderTravelerRow(
      { ...traveler, birthdate: values.birthdate },
      { departureDate },
    ).isChild;
    await onSubmit({
      ...(isEdit ? { id: traveler?.id } : {}),
      gender: values.gender,
      firstName: values.firstName,
      lastName: values.lastName,
      nationality: values.nationality,
      passportNumber: values.passportNumber,
      // Dates go back only when changed: the API's UTC midnight read in a
      // timezone behind UTC would otherwise shift them a day on each save.
      ...(!isEdit ||
      toApiDate(values.passportExpirationDate) !==
        toApiDate(initialValues.passportExpirationDate)
        ? {
            passportExpirationDate: toApiDate(values.passportExpirationDate),
          }
        : {}),
      ...(birthdateChanged ? { birthdate: toApiDate(values.birthdate) } : {}),
      ...(isChild &&
      (birthdateChanged || values.ageIsExact !== initialValues.ageIsExact)
        ? { ageIsExact: values.ageIsExact }
        : {}),
    });
  };

  const subtitle = current
    ? [
        current.name || '--',
        current.isChild ? t('travelers.child') : t('travelers.adult'),
      ].join(' · ')
    : t('travelers.addSubtitle');

  return (
    <KoobModal
      open={open}
      setOpen={setOpen}
      widthClass="sm:max-w-[640px]"
      paddingClass="p-0"
      autoFocus={false}
    >
      <Formik<FormValues>
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, dirty, isSubmitting }) => {
          const row = getFolderTravelerRow(
            {
              ...traveler,
              birthdate: values.birthdate,
              passportExpirationDate: values.passportExpirationDate,
            },
            { departureDate, returnDate },
          );

          return (
            <Form>
              <div className="flex items-start gap-3 border-0 border-b border-solid border-gray-100 px-[22px] pb-4 pt-5">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <h2 className="m-0 text-[17px] font-bold text-k-h">
                    {isEdit
                      ? t('travelers.editTitle')
                      : t('travelers.addTitle')}
                  </h2>
                  <span className="text-[12.5px] text-gray-500">
                    {subtitle}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label={t('misc:close')}
                  className="cursor-pointer border-none bg-transparent p-1 text-base leading-none text-gray-400 hover:text-gray-600"
                >
                  <i className="fa-regular fa-xmark" aria-hidden="true" />
                </button>
              </div>

              <div className="flex flex-col gap-5 px-[22px] py-5">
                <section className="flex flex-col gap-3">
                  <SectionTitle>{t('travelers.identity')}</SectionTitle>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-[150px_minmax(0,1fr)_minmax(0,1fr)]">
                    <Field label={t('travelers.gender')}>
                      <KoobInputSelect
                        options={GENDERS.map((value) => ({
                          value,
                          label: t(GENDER_LABEL_KEY[value]),
                        }))}
                        value={values.gender}
                        onValueChange={(value: string) =>
                          setFieldValue('gender', value ?? null)
                        }
                        data-testid="folder-traveler-gender"
                      />
                    </Field>
                    <Field label={t('travelers.firstName')}>
                      <FolderFormTextField
                        name="firstName"
                        testId="folder-traveler-first-name"
                      />
                    </Field>
                    <Field label={t('travelers.lastName')}>
                      <FolderFormTextField
                        name="lastName"
                        testId="folder-traveler-last-name"
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field
                      label={
                        <span className="flex items-center gap-1.5">
                          {t('travelers.birthdate')}
                          <i
                            className="fa-regular fa-circle-info cursor-help text-[11px] text-gray-400"
                            title={t('travelers.ageAtDepartureHint')}
                            aria-hidden="true"
                          />
                        </span>
                      }
                    >
                      <KoobInputDateField
                        name="birthdate"
                        testId="folder-traveler-birthdate"
                      />
                      <span className="text-[11.5px] text-gray-500">
                        {row.ageAtDeparture !== null && departureDate
                          ? t('travelers.ageAtDeparture', {
                              count: row.ageAtDeparture,
                              date: formatDate(departureDate, 'DD/MM/YYYY'),
                              tag: row.isChild
                                ? t('travelers.child')
                                : t('travelers.adult'),
                            })
                          : t('travelers.ageAtDepartureHint')}
                      </span>
                      {row.isChild && (
                        <KoobInputCheckbox
                          size="sm"
                          checked={values.ageIsExact}
                          onCheckedChange={(checked) =>
                            setFieldValue('ageIsExact', checked)
                          }
                          label={
                            <span className="text-xs font-normal text-gray-700">
                              {t('definitiveDate')}
                            </span>
                          }
                          data-testid="folder-traveler-definitive-birthdate"
                        />
                      )}
                    </Field>
                    <Field label={t('travelers.nationality')}>
                      <KoobInputSelect
                        options={nationalityOptions}
                        value={values.nationality}
                        searchable
                        onValueChange={(value: string) =>
                          setFieldValue('nationality', value ?? null)
                        }
                        data-testid="folder-traveler-nationality"
                      />
                    </Field>
                  </div>
                </section>

                <section className="flex flex-col gap-3 border-0 border-t border-solid border-gray-100 pt-[18px]">
                  <SectionTitle>{t('travelers.passport')}</SectionTitle>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label={t('travelers.passportNumber')}>
                      <FolderFormTextField
                        name="passportNumber"
                        testId="folder-traveler-passport-number"
                      />
                    </Field>
                    <Field label={t('travelers.expirationDate')}>
                      <KoobInputDateField
                        name="passportExpirationDate"
                        warning={row.expiresBeforeReturn}
                        testId="folder-traveler-passport-expiration"
                      />
                      {row.expiresBeforeReturn && (
                        <span className="flex items-center gap-1.5 text-[11.5px] font-semibold text-amber-700">
                          <i
                            className="fa-solid fa-triangle-exclamation text-[11px]"
                            aria-hidden="true"
                          />
                          {t('travelers.expiresBeforeReturn', {
                            date: formatDate(returnDate, 'DD/MM/YYYY'),
                          })}
                        </span>
                      )}
                    </Field>
                  </div>
                </section>
              </div>

              <div className="flex justify-end gap-2.5 border-0 border-t border-solid border-gray-100 bg-gray-50 px-[22px] py-3.5">
                <KoobButton
                  type="button"
                  variant="secondary"
                  onClick={() => setOpen(false)}
                  data-testid="folder-traveler-cancel"
                >
                  {t('misc:cancel')}
                </KoobButton>
                <KoobButton
                  type="submit"
                  variant="primary"
                  disabled={!dirty || isSubmitting}
                  data-testid="folder-traveler-save"
                >
                  {isEdit ? t('misc:saveChanges') : t('travelers.addCta')}
                </KoobButton>
              </div>
            </Form>
          );
        }}
      </Formik>
    </KoobModal>
  );
}
